import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getUserSubscription } from "../services/subscription";
import { trackPurchase } from "../utils/metaPixel";
import { trackTikTokCompletePayment } from "../utils/tiktokPixel";
import { toast } from "sonner";
import styles from "../styles/stripe-success.module.css";
import { CheckCircle, ArrowRight } from "react-feather";

const StripeSuccess = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Get session_id from URL query params or from location state
  const searchParams = new URLSearchParams(location.search);
  const sessionId =
    searchParams.get("session_id") ||
    (location.state && location.state.sessionId);

  useEffect(() => {
    // If not authenticated, redirect to login
    if (!isAuthenticated) {
      navigate("/auth", { state: { redirectTo: "/account" } });
      return;
    }

    const fetchSubscriptionDetails = async () => {
      try {
        setIsLoading(true);

        // Wait a moment to allow the webhook to process
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Fetch the user's subscription
        const subscriptionData = await getUserSubscription(user.id);

        // We don't need to store the subscription anymore since we're not displaying plan details
        if (subscriptionData) {
          if (
            subscriptionData.status === "active" ||
            subscriptionData.status === "trialing"
          ) {
            // session_id as eventID so Meta/TikTok dedupe StrictMode/remount double-fires
            trackPurchase(subscriptionData.price_id, sessionId);
            trackTikTokCompletePayment(subscriptionData.price_id, sessionId);
          }
          toast.success("Subscription activated successfully!");
        } else {
          toast.error("There was an issue verifying your subscription");
        }
      } catch (error) {
        console.error("Error fetching subscription details:", error);
        toast.error("There was an issue verifying your subscription");
      } finally {
        setIsLoading(false);
      }
    };

    if (sessionId && user) {
      fetchSubscriptionDetails();
    } else {
      setIsLoading(false);
    }
  }, [sessionId, user, isAuthenticated, navigate]);

  return (
    <div className={styles.successContainer}>
      <div className={styles.successCard}>
        {isLoading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Activating your subscription...</p>
          </div>
        ) : (
          <>
            <div className={styles.successIcon}>
              <CheckCircle size={48} />
            </div>
            <h1 className={styles.successTitle}>Payment Successful!</h1>
            <p className={styles.successMessage}>
              Thank you for subscribing! Your account has been activated.
            </p>
            <div className={styles.actionButtons}>
              <Link to="/home" className={styles.primaryButton}>
                Go to Dashboard <ArrowRight size={16} />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StripeSuccess;
